import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { getDatosTimbradoRecibo } from 'api/nominas/timbrado';
import currencyFormatter from 'utils/currencyFormatter';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import { presentacionEstatusTimbrado } from 'utils/estatusTimbrado';
import { ESTATUS_TIMBRADO_TIMBRADO } from 'utils/constants';
import { hasActionPermission } from 'utils/permission';
import HistorialIntentosTimbrado from 'components/Nominas/Timbrado/HistorialIntentosTimbrado';
import CancelacionesTimbrado from 'components/Nominas/Timbrado/CancelacionesTimbrado';

const { Text } = Typography;

const SIN_DATO = '—';

// Ticket 36: el historial exige el mismo permiso que valida el servidor (T34)
export const PERMISO_HISTORIAL_INTENTOS = { model: 'intentotimbradosysticloud', action: 'view' };
// Ticket 38: cada sección de Cancelaciones exige el mismo permiso que valida el servidor (T32)
export const PERMISO_SOLICITUDES_CANCELACION = { model: 'cancelacionsysticloud', action: 'view' };
export const PERMISO_CONSULTAS_CANCELACION = {
  model: 'consultaestadocancelacionsysticloud', action: 'view',
};

const formatearFecha = (valor) => {
  if (!valor) return SIN_DATO;
  const fecha = moment(valor);
  return fecha.isValid() ? fecha.format(DATE_FRONTEND_FORMAT) : SIN_DATO;
};

const formatearImporte = (valor) => {
  if (valor === null || valor === undefined || valor === '') return SIN_DATO;
  const numero = Number(valor);
  return Number.isNaN(numero) ? SIN_DATO : currencyFormatter(numero);
};

// Descarga local del XML: no se envía a ningún servicio
const descargarXml = (xml, nombre) => {
  const blob = new Blob([xml], { type: 'application/xml' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const DatosTimbradoRecibo = ({
  visible,
  onClose,
  recibo,
  estatusTimbrado,
}) => {
  const [loading, setLoading] = useState(false);
  // reciboId indica a qué recibo pertenecen los datos, para no mostrar los de otro
  const [datos, setDatos] = useState(null);
  const [verXml, setVerXml] = useState(false);
  const [pestana, setPestana] = useState('datos');
  const reciboId = recibo?.id;
  const verHistorial = hasActionPermission(
    PERMISO_HISTORIAL_INTENTOS.model,
    PERMISO_HISTORIAL_INTENTOS.action,
  );
  const verSolicitudes = hasActionPermission(
    PERMISO_SOLICITUDES_CANCELACION.model,
    PERMISO_SOLICITUDES_CANCELACION.action,
  );
  const verConsultas = hasActionPermission(
    PERMISO_CONSULTAS_CANCELACION.model,
    PERMISO_CONSULTAS_CANCELACION.action,
  );
  const verCancelaciones = verSolicitudes || verConsultas;
  const conPestanas = verHistorial || verCancelaciones;

  useEffect(() => {
    let mounted = true;
    setVerXml(false);
    setPestana('datos');
    if (!visible || !reciboId) {
      setDatos(null);
      return undefined;
    }
    const fetchDatos = async () => {
      setLoading(true);
      const respuesta = await getDatosTimbradoRecibo(reciboId);
      if (mounted) {
        setDatos({ ...respuesta, reciboId });
        setLoading(false);
      }
    };
    fetchDatos();
    return () => { mounted = false; };
  }, [visible, reciboId]);

  const datosListos = !!datos && datos.reciboId === reciboId;
  const { registro, total, error } = datosListos ? datos : {};
  const estatus = presentacionEstatusTimbrado(registro?.estatusId, estatusTimbrado);
  // El XML solo es válido para recibos timbrados
  const xmlDisponible = registro?.estatusId === ESTATUS_TIMBRADO_TIMBRADO
    && typeof registro?.xml === 'string' && registro.xml !== '';
  const nombreXml = registro?.uuid ? `${registro.uuid}.xml` : `recibo-${reciboId}.xml`;

  const contenido = () => {
    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message="No fue posible consultar la información de timbrado del recibo."
        />
      );
    }
    if (!registro) {
      return <Empty description="Este recibo no tiene información de timbrado" />;
    }
    return (
      <>
        {total > 1 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Existen ${total} registros de timbrado; se muestra el más reciente.`}
          />
        )}
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="UUID">
            {registro.uuid ? <Text copyable>{registro.uuid}</Text> : SIN_DATO}
          </Descriptions.Item>
          <Descriptions.Item label="RFC Receptor">
            {registro.rfcReceptor || SIN_DATO}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha de Timbrado">
            {formatearFecha(registro.fechaTimbrado)}
          </Descriptions.Item>
          <Descriptions.Item label="Importe Timbrado">
            {formatearImporte(registro.importe)}
          </Descriptions.Item>
          <Descriptions.Item label="Estatus de Timbrado">
            {estatus.conDescripcion
              ? <Tag color={estatus.color}>{estatus.texto}</Tag>
              : estatus.texto}
          </Descriptions.Item>
          <Descriptions.Item label="Mensaje de Timbrado">
            {registro.mensaje || SIN_DATO}
          </Descriptions.Item>
        </Descriptions>
        <Space style={{ marginTop: 16 }}>
          <Button
            icon={<DownloadOutlined />}
            disabled={!xmlDisponible}
            onClick={() => descargarXml(registro.xml, nombreXml)}
          >
            Descargar XML
          </Button>
          <Button
            icon={<EyeOutlined />}
            disabled={!xmlDisponible}
            onClick={() => setVerXml((v) => !v)}
          >
            {verXml && xmlDisponible ? 'Ocultar XML' : 'Ver XML'}
          </Button>
        </Space>
        {verXml && xmlDisponible && (
          // Solo texto: React escapa el contenido; nunca interpretarlo como HTML
          <pre
            style={{
              marginTop: 16,
              maxHeight: 400,
              overflow: 'auto',
              padding: 12,
              background: '#f5f5f5',
              border: '1px solid #d9d9d9',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              fontSize: 12,
            }}
          >
            {registro.xml}
          </pre>
        )}
      </>
    );
  };

  const datosTimbrado = (
    <Spin tip="Cargando..." spinning={loading}>
      {!loading && datosListos ? contenido() : <div style={{ minHeight: 80 }} />}
    </Spin>
  );

  return (
    <Drawer
      title="Datos de Timbrado"
      placement="right"
      width={conPestanas ? 900 : 620}
      onClose={onClose}
      visible={visible}
      destroyOnClose
    >
      {recibo && (
        <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Empleado">
            {recibo.empleado_numero_y_nombre_completo || SIN_DATO}
          </Descriptions.Item>
          <Descriptions.Item label="No. de Plaza">
            {recibo.plaza_no || SIN_DATO}
          </Descriptions.Item>
          <Descriptions.Item label="Importe Neto">
            {formatearImporte(recibo.importe_neto)}
          </Descriptions.Item>
        </Descriptions>
      )}
      {conPestanas ? (
        // El historial se monta (y se consulta) solo al abrir su pestaña
        <Tabs
          activeKey={pestana}
          onChange={setPestana}
          items={[
            { key: 'datos', label: 'Datos', children: datosTimbrado },
            verHistorial && {
              key: 'historial',
              label: 'Historial de Intentos',
              children: <HistorialIntentosTimbrado reciboId={reciboId} />,
            },
            verCancelaciones && {
              key: 'cancelaciones',
              label: 'Cancelaciones',
              // Solo existe mientras está activa: se vuelve a consultar cada vez que se abre
              children: pestana === 'cancelaciones' ? (
                <CancelacionesTimbrado
                  reciboId={reciboId}
                  verSolicitudes={verSolicitudes}
                  verConsultas={verConsultas}
                />
              ) : null,
            },
          ].filter(Boolean)}
        />
      ) : datosTimbrado}
    </Drawer>
  );
};

DatosTimbradoRecibo.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recibo: PropTypes.shape({
    id: PropTypes.number,
    empleado_numero_y_nombre_completo: PropTypes.string,
    plaza_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    importe_neto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  estatusTimbrado: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })),
};

DatosTimbradoRecibo.defaultProps = {
  recibo: null,
  estatusTimbrado: [],
};

export default DatosTimbradoRecibo;
